import { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useAtomValue } from 'jotai';
import { FilterTabType } from 'src/components/CombinedFilterModal/context';
import useUserData from 'src/hooks/useUserData';
import { statusAtom } from 'src/stores/match/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { MatchFilterOption, STATUS } from 'src/types/Match';
import { hexToRgb } from 'src/utils/common';
import { pushEvent } from 'src/utils/event-gateway';
import { mouseHoverCss } from 'src/utils/styled';
import useGenderPreference, { FREE_GENDER_FILTER_OPTIONS } from './useGenderPreference';
const Title = styled.div`
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
`;
const Icon = styled.img`
  width: 16px;
  height: 8px;
  top: 12px;
  right: 12px;
  position: absolute;
`;
const Wrap = styled.button<{
  isActive: boolean;
}>`
  all: unset;
  cursor: pointer;
  padding-top: 16px;
  padding-bottom: 16px;
  box-sizing: border-box;
  border-radius: 16px;
  border-width: 2px;
  border-style: solid;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  ${({
  theme
}) => mouseHoverCss(`
      background-color: rgba(${hexToRgb(theme.color.primary1)}, 0.2);
    );
  `)}
  ${({
  theme,
  isActive
}) => isActive ? `
    background-color: rgba(${hexToRgb(theme.color.primary1)}, 0.2);
    border-color: ${theme.color.primary1};
    color: ${theme.color.content12};
    ${Title} {
      font-weight: 700;
    }
  ` : `
    background-color: ${theme.color.surface4};
    border-color: transparent;
    color: ${theme.color.content7};
  `}
  ${({
  theme
}) => theme.screenSize.tablet} {
    height: 130px;
  }
`;
const ImgBase = css`
  width: 64px;
  height: 64px;
`;
const ImgPicture = styled.picture`
  ${ImgBase}
  margin-bottom: 18px;
  ${({
  theme
}) => theme.screenSize.tablet} {
    margin-bottom: 12px;
  }
`;
const ImgSource = styled.source`
  ${ImgBase};
`;
const Img = styled.img`
  ${ImgBase};
`;
interface Props {
  filterOption: MatchFilterOption;
  isActive: boolean;
  onClick: () => void;
  /**
   * 해당 UI가 위치한 필터 탭 정보 (특정 탭에 종속된 게 아니면 생략)
   */
  filterTab?: FilterTabType;
  className?: string;
}
const GenderOptionItem: FC<Props> = ({
  filterOption,
  isActive,
  onClick,
  className,
  filterTab
}) => {
  const status = useAtomValue(statusAtom);
  const gender = filterOption.key;
  const key = `GenderPreferenceModal-${gender}`;
  const {
    userProfile
  } = useUserData() || {};
  const {
    genderMatchFilter,
    getRequiredGemsForGenderOption
  } = useGenderPreference();
  if (!genderMatchFilter || !userProfile) return null;
  const requiredGemCost = getRequiredGemsForGenderOption(filterOption);
  const handleClick = () => {
    if (!isActive) {
      const landFrom = status === STATUS.INITIAL ? 'MIRROR' : 'FINDING';
      const eventName = EVENT_NAME[`${landFrom}__FILTER_${filterOption.key as 'DEFAULT'}`];
      const eventParams = {
        type: filterTab || 'none',
        // 해당 필터 옵션 관련 event name이 명시적으로 정의되어 있지 않으면 파라미터로 정보 전달
        ...(eventName ? {} : {
          value: filterOption.key
        })
      };
      pushEvent({
        eventType: EVENT_TYPE.VIDEO_CHAT,
        eventName: eventName || EVENT_NAME[`${landFrom}__FILTER_DEFAULT`],
        eventParams
      });
    }
    onClick();
  };
  const isFree = FREE_GENDER_FILTER_OPTIONS.includes(gender);
  return <Wrap isActive={isActive} onClick={handleClick} className={className} data-testid={'Gender-Filter-Option'} data-gemCost={requiredGemCost} data-isActive={isActive} data-gender={gender} data-sentry-element="Wrap" data-sentry-component="GenderOptionItem" data-sentry-source-file="OptionItem.tsx">
      {!isFree && <Icon src='/vividi/images/icons/icUnlimited.png' alt='filter for unlimited' />}
      <ImgPicture data-sentry-element="ImgPicture" data-sentry-source-file="OptionItem.tsx">
        <ImgSource srcSet={`/images/match/${key}.png`} type='image/png' data-sentry-element="ImgSource" data-sentry-source-file="OptionItem.tsx" />
        <Img src={`/images/match/${key}.png`} alt={`Gender prefer ${key}`} data-sentry-element="Img" data-sentry-source-file="OptionItem.tsx" />
      </ImgPicture>
      <Title data-testid={'Gender-Filter-Option-Title'} data-sentry-element="Title" data-sentry-source-file="OptionItem.tsx">{filterOption.displayName}</Title>
    </Wrap>;
};
export default GenderOptionItem;